import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency } from "@/lib/formatters";
import MetricCard from "@/components/shared/MetricCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowDownLeft, ArrowUpRight, TrendingUp, DollarSign, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip as ReTooltip, Legend,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs" style={{ color: p.stroke || p.fill }}>{p.name}: {formatCurrency(p.value)}</p>
      ))}
    </div>
  );
};

export default function Cashflow() {
  const [cashflows, setCashflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    API.get("/cashflows")
      .then((res) => setCashflows(res.data.cashflows || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const totalCalls = cashflows.reduce((a, c) => a + c.capital_calls, 0);
  const totalDist = cashflows.reduce((a, c) => a + c.distributions, 0);
  const netCf = totalDist - totalCalls;
  const latest = cashflows[cashflows.length - 1];

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="cashflow-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Cashflow Analysis</h1>
          <p className="text-sm text-muted-foreground mt-1">Capital calls, distributions, and net cashflow timeline</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-cashflow-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-cashflow-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total Capital Calls" value={formatCurrency(totalCalls)} icon={ArrowDownLeft} />
        <MetricCard label="Total Distributions" value={formatCurrency(totalDist)} icon={ArrowUpRight} />
        <MetricCard label="Net Cashflow" value={formatCurrency(netCf)} icon={TrendingUp} />
        <MetricCard label="Latest Year Net" value={formatCurrency(latest?.net_cashflow)} icon={DollarSign} />
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <CardTitle className="text-sm font-semibold">Capital Calls vs Distributions</CardTitle>
        </CardHeader>
        <CardContent className="p-4" data-testid="cashflow-bar-chart">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={cashflows} barGap={4}>
              <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCurrency(v)} />
              <ReTooltip content={<CustomTooltip />} />
              <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
              <Bar dataKey="capital_calls" name="Capital Calls" fill="#EF4444" radius={[2, 2, 0, 0]} barSize={28} />
              <Bar dataKey="distributions" name="Distributions" fill="#10B981" radius={[2, 2, 0, 0]} barSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <CardTitle className="text-sm font-semibold">Cumulative Cashflow</CardTitle>
        </CardHeader>
        <CardContent className="p-4" data-testid="cumulative-chart">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={cashflows}>
              <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCurrency(v)} />
              <ReTooltip content={<CustomTooltip />} />
              <Legend formatter={(v) => <span className="text-xs text-muted-foreground">{v}</span>} />
              <Area type="monotone" dataKey="cumulative_invested" name="Cumulative Invested" stroke="#EF4444" fill="#EF4444" fillOpacity={0.1} strokeWidth={2} />
              <Area type="monotone" dataKey="cumulative_distributed" name="Cumulative Distributed" stroke="#10B981" fill="#10B981" fillOpacity={0.1} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <CardTitle className="text-sm font-semibold">Annual Cashflow Detail</CardTitle>
        </CardHeader>
        <CardContent className="p-0" data-testid="cashflow-table">
          <table className="w-full">
            <thead><tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Year</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Capital Calls</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Distributions</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Net Cashflow</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Cum. Invested</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Cum. Distributed</th>
            </tr></thead>
            <tbody>
              {cashflows.map((cf, i) => (
                <tr key={i} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-2.5 text-sm font-data">{cf.year}</td>
                  <td className="px-4 py-2.5 text-sm font-data text-right text-red-500">{formatCurrency(cf.capital_calls)}</td>
                  <td className="px-4 py-2.5 text-sm font-data text-right text-emerald-500">{formatCurrency(cf.distributions)}</td>
                  <td className={`px-4 py-2.5 text-sm font-data text-right ${cf.net_cashflow >= 0 ? "text-emerald-500" : "text-red-500"}`}>{formatCurrency(cf.net_cashflow)}</td>
                  <td className="px-4 py-2.5 text-sm font-data text-right">{formatCurrency(cf.cumulative_invested)}</td>
                  <td className="px-4 py-2.5 text-sm font-data text-right">{formatCurrency(cf.cumulative_distributed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <AddEditDialog open={showAdd} entityType="cashflows" onClose={() => setShowAdd(false)} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="cashflows" onClose={() => setShowUpload(false)} onSuccess={refresh} />
    </div>
  );
}
